import React, { useState, useMemo, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { ThemeProvider as StyledThemeProvider } from 'styled-components';
import CssBaseline from '@mui/material/CssBaseline';
import { getTheme } from './styles/theme';
import { GlobalStyles } from './styles/GlobalStyles';
import { LanguageProvider } from './contexts/LanguageContext';
import Layout from './components/Layout/Layout';
import Home from './pages/Home/Home';
import Auth from './pages/Auth/Auth';
import Wallets from './pages/Wallets/Wallets';
import Dashboard from './pages/Dashboard/Dashboard';
import Payment from './pages/Payment/Payment';
import Settings from './pages/Settings/Settings';
import About from './pages/About/About';
import Terms from './pages/Terms/Terms';
import Privacy from './pages/Privacy/Privacy';
import Contact from './pages/Contact/Contact';
import FAQ from './pages/FAQ/FAQ';
import Blog from './pages/Blog/Blog';
import { getCurrentAuthUser } from './utils/auth';
import { initializeGA, trackPageView } from './utils/analytics';
import ProtectedRoute from './components/ProtectedRoute/ProtectedRoute';
import TeamProtectedRoute from './components/TeamProtectedRoute/TeamProtectedRoute';
import NotFound from './pages/NotFound/NotFound';

const THEME_KEY = 'xch_theme_preference';

// Analytics tracking component
const AnalyticsTracker = () => {
  const location = useLocation();

  useEffect(() => {
    trackPageView(location.pathname);
  }, [location]);

  return null;
};

const App: React.FC = () => {
  // Initialize theme from localStorage or default to false (light theme)
  const [isDarkMode, setIsDarkMode] = useState<boolean>(() => {
    const savedTheme = localStorage.getItem(THEME_KEY);
    return savedTheme ? JSON.parse(savedTheme) : false;
  });

  const [userEmail, setUserEmail] = useState<string | null>(null);

  // Initialize Google Analytics
  useEffect(() => {
    const measurementId = process.env.REACT_APP_GA_MEASUREMENT_ID;
    if (measurementId) {
      initializeGA(measurementId);
    }
  }, []);

  // Check for authenticated user on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        console.log('Checking auth status on page load...');
        const user = await getCurrentAuthUser();
        if (user?.username) {
          console.log('Page load - Setting user email:', user.username);
          setUserEmail(user.username);
        } else {
          console.log('No authenticated user found');
          setUserEmail(null);
        }
      } catch (error) {
        console.error('Error checking auth status:', error);
        setUserEmail(null);
      }
    };
    checkAuth();
  }, []);

  // Save theme preference whenever it changes
  useEffect(() => {
    localStorage.setItem(THEME_KEY, JSON.stringify(isDarkMode));
  }, [isDarkMode]);

  const toggleTheme = (): void => {
    setIsDarkMode((prevMode: boolean): boolean => !prevMode);
  };

  const handleAuthSuccess = (email: string) => {
    setUserEmail(email);
  };

  const currentTheme = useMemo(() => {
    return getTheme(isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  return (
    <LanguageProvider>
      <Router>
        <MuiThemeProvider theme={currentTheme}>
          <StyledThemeProvider theme={currentTheme}>
            <CssBaseline />
            <GlobalStyles />
            <AnalyticsTracker />
            <Routes>
              {/* 404 Routes - Outside TeamProtectedRoute */}
              <Route path="/not-found" element={
                <Layout toggleTheme={toggleTheme} isDarkMode={isDarkMode} userEmail={userEmail} setUserEmail={setUserEmail}>
                  <NotFound />
                </Layout>
              } />
              
              {/* All other routes - Inside TeamProtectedRoute */}
              <Route path="*" element={
                <TeamProtectedRoute>
                  <Layout toggleTheme={toggleTheme} isDarkMode={isDarkMode} userEmail={userEmail} setUserEmail={setUserEmail}>
                    <Routes>
                      <Route path="/" element={<Home />} />
                      <Route path="/auth" element={<Auth />} />
                      <Route path="/privacy" element={<Privacy />} />
                      <Route path="/terms" element={<Terms />} />
                      <Route path="/about" element={<About />} />
                      <Route path="/contact" element={<Contact />} />
                      <Route path="/faq" element={<FAQ />} />
                      <Route path="/blog" element={<Blog />} />
                      <Route path="/blog/:blogId" element={<Blog />} />
                      
                      {/* Protected Routes */}
                      <Route path="/wallets" element={
                        <ProtectedRoute>
                          <Wallets />
                        </ProtectedRoute>
                      } />
                      <Route path="/payment" element={
                        <ProtectedRoute>
                          <Payment />
                        </ProtectedRoute>
                      } />
                      <Route path="/dashboard/:walletId" element={
                        <ProtectedRoute>
                          <Dashboard />
                        </ProtectedRoute>
                      } />
                      <Route path="/settings/:walletId" element={
                        <ProtectedRoute>
                          <Settings />
                        </ProtectedRoute>
                      } />
                      
                      {/* Catch-all route */}
                      <Route path="*" element={<Navigate to="/not-found" replace />} />
                    </Routes>
                  </Layout>
                </TeamProtectedRoute>
              } />
            </Routes>
          </StyledThemeProvider>
        </MuiThemeProvider>
      </Router>
    </LanguageProvider>
  );
};

export default App;
